import { useToast } from '../../../components/ui/ToastProvider'
import { VolumePricingPopoverShell } from '../../../components/ui/VolumePricingPopoverShell'
import type { LibrarySourceable, VolumePricingCurve } from '../types'
import { addLibraryPartToCart } from '../volume-pricing'

export interface VolumePricingCellTriggerProps {
  readonly partName: string
  /** The row's current requested quantity — drives both the previewed
   * unit-price/total and the "Add {quantity} to cart" CTA. */
  readonly quantity: number
  readonly currency: string
  readonly curve: VolumePricingCurve
  readonly sourceable: LibrarySourceable
  /** "Request volume RFQ" when `quantity` is above `curve.cap` (ADR-0058
   * §3 — the server never fabricates a price above cap, so the popover
   * must not either). Wired by the caller to the SAME queue-into-`/rfq/new`
   * path the Part-detail auto-priced curve card's "Request volume RFQ"
   * (`PartDetailPage.tsx`'s `PricingCard`) and the Library's "Add to RFQ
   * bundle" CTA use (`PartsLibraryPage.handleAddToRfq`) — not a new RFQ
   * creation path. */
  readonly onRequestRfq: () => void
}

/**
 * The "Volume pricing" trigger + popover (UID-159 / A3, AC1,
 * `dc.html:1310-1332`). Mounted by `PartsTable`'s Unit price cell (moved
 * there from the now-removed Qty column, UID-386) ONLY when
 * `pricingProjection.volumeCurve` is present (ADR-0058: the curve exists
 * only for `auto_priced` parts) — otherwise nothing renders below the price.
 *
 * A THIN WRAPPER (ADR-0077 §D2/§D3) over the shared, vertical-agnostic
 * `VolumePricingPopoverShell` (`components/ui/VolumePricingPopoverShell.tsx`),
 * which owns the `useAnchoredPopover` + `useFocusTrap` anchoring/dialog
 * wiring this component used to own directly. This wrapper injects the
 * parts-vertical-specific pieces only: the trigger button's look,
 * and `addLibraryPartToCart(sourceable, quantity)` as the `onAdd` action —
 * BYTE-IDENTICAL to the pre-refactor behavior (same call, same toast, same
 * close-on-success; see `VolumePricingPopover.test.tsx`'s characterization
 * tests, unchanged by this refactor).
 */
export function VolumePricingCellTrigger({
  partName,
  quantity,
  currency,
  curve,
  sourceable,
  onRequestRfq,
}: VolumePricingCellTriggerProps) {
  const { showToast } = useToast()

  return (
    <VolumePricingPopoverShell
      heading="Volume pricing"
      quantity={quantity}
      currency={currency}
      curve={curve}
      onAdd={() => addLibraryPartToCart(sourceable, quantity)}
      onAddSuccess={() => showToast('Added to cart.', 'success')}
      onAddError={() => showToast('Could not add to cart. Please try again.', 'error')}
      onRequestRfq={onRequestRfq}
      renderTrigger={({ ref, ariaHasPopup, ariaExpanded, toggle }) => (
        <button
          ref={ref}
          type="button"
          className="volume-pricing-trigger"
          // UID-407 code-review finding (W1): `partName` was declared on
          // this component's props but never used, so every curve-bearing
          // row's trigger shared the SAME accessible name ("Volume
          // pricing") — ambiguous once a second table (`WorkspacePartsTab`)
          // started reusing this component for potentially many rows at
          // once. `PartRowActions`' own "More actions for {partName}"
          // trigger already does this correctly; mirrored here.
          aria-label={`Volume pricing for ${partName}`}
          aria-haspopup={ariaHasPopup}
          aria-expanded={ariaExpanded}
          onClick={toggle}
        >
          <VolumeCurveMiniIcon />
          Volume pricing
        </button>
      )}
    />
  )
}

function VolumeCurveMiniIcon() {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3v18h18" />
      <path d="M19 9c-2.5 0-3.5 6-7 6s-4.5-4-9-3.5" />
    </svg>
  )
}
